//** React Import */
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";

//** Utils Import */
import { loadFont, optimizeFontSizeBinarySearch } from "../../../../Utils/Ultility";
import { applySvgLabelPreview } from "../../../../Utils/svgLabelPreview";

//** Style Import */
import "./style.css";
import PropTypes from "prop-types";

const LabelPreview = ({ data, design, type }) => {
  const [text, setText] = useState(data.text);
  const svgRef = useRef(null);
  const useSvg = useSelector((state) => state.shop.svgLabelBadge);

  const dynamicText = [
    "{SAVE_PERCENT}",
    "{SAVE_AMOUNT}",
    "{PRICE}",
    "{NEW_FOR}",
    "{STOCK}",
    "{MIN_PRICE}",
    "{MAX_PRICE}",
    "{MIN_SALE}",
    "{MAX_SALE}",
    "{NUMBER_OF_REVIEW}",
    "{AVERAGE_RATING}",
  ];

  useEffect(() => {
    if (data.text) {
      let labelTextTmp = data.text;
      for (let i = 0; i < dynamicText.length; i++) {
        if (data.text.includes(dynamicText[i])) {
          labelTextTmp = labelTextTmp.replace(dynamicText[i], "XX");
        }
      }
      setText(labelTextTmp);
    } else {
      setText(data.text);
    }
  }, [data]);

  // SVG branch: one node, drawn entirely by the shared module. This is a thumbnail, so it always
  // shows the base (collection/desktop) size — the per-screen values belong in the size editor.
  useEffect(() => {
    if (!useSvg) return;
    applySvgLabelPreview(svgRef.current, {
      data,
      design,
      type,
      page: "collection",
      isMobile: false,
      text: data[`${type}_type`] === 1 ? text ?? "" : "",
      width: data[`${type}_width`],
      height: data[`${type}_height`],
    });
  }, [data, design, text, type, useSvg]);

  useEffect(() => {
    if (useSvg) return;
    if (data[`${type}_type`] === 1) {
      document.querySelectorAll(`#data-${data.id}`).forEach((presetPreview) => {
        if (data[`${type}_text_type`] === 0 && design?.shape_color?.length) {
          presetPreview.style.backgroundColor = `${design.shape_color}`;
        }
        if (
          data[`${type}_text_type`] === 1 &&
          design?.shape_start_color?.length &&
          design?.shape_ending_color?.length
        ) {
          presetPreview.style.background = `linear-gradient(${design.shape_gradient_direction}deg, ${design.shape_start_color}, ${design.shape_ending_color})`;
        }
        const textElement = presetPreview.querySelector(".pl-text");
        textElement.style.color = `${design?.text_color}`;
        if (design?.font_family && design?.font_family != "default") {
          loadFont(design.font_family);
          textElement.style.fontFamily = `'${design.font_family}'`;
        }
        textElement.style.wordBreak = "break-word";
        textElement.innerHTML = text;
        let initialFontSize = parseFloat(window.getComputedStyle(textElement).fontSize);
        let targetHeight = presetPreview.clientHeight * (data.responsive_ratio || 1);

        if (targetHeight > 0 && initialFontSize > 0) {
          optimizeFontSizeBinarySearch(textElement, targetHeight, initialFontSize);
        }
        if (data.image) {
          let image = presetPreview.querySelector("img");
          image.onload = () => {
            targetHeight = image.clientHeight * (data.responsive_ratio || 1);
            optimizeFontSizeBinarySearch(textElement, targetHeight, initialFontSize);
          };
        }
      });
    }
  }, [data, design, text, useSvg]);

  if (useSvg) {
    return (
      <div className="Deco-Manage-Preview">
        <svg
          ref={svgRef}
          id={`data-${data.id}`}
          className="Deco-Manage-Preview__text-preview"
          style={{ display: "block", overflow: "visible", rotate: `${data.rotate}deg` }}
        />
      </div>
    );
  }

  return (
    <div className="Deco-Manage-Preview">
      <div
        id={`data-${data.id}`}
        className={`Deco-Manage-Preview__text-preview ${data.image ? "" : design?.shape_class}`}
        style={{
          backgroundColor: data.image ? "transparent" : design?.shape_color,
          borderStyle: design?.border?.style,
          borderWidth: design?.border?.width,
          borderColor: design?.border?.color,
          borderRadius: design?.border_radius,
          aspectRatio: data.image ? "unset" : data[`${type}_width`] / data[`${type}_height`],
          rotate: `${data.rotate}deg`,
        }}
      >
        {data.image && <img className="pl-w-100" src={data.image} alt={data.name} loading="lazy" />}
        {data[`${type}_type`] === 1 && (
          <span
            className="Deco-Manage-Preview__text-content pl-text"
            style={{
              color: design?.text_color,
              fontStyle: design?.font_style?.style,
              textDecoration: design?.font_style?.decoration,
              fontWeight: design?.font_style?.weight,
            }}
          ></span>
        )}
      </div>
    </div>
  );
};

LabelPreview.propTypes = {
  data: PropTypes.shape({
    id: PropTypes.number,
    text: PropTypes.string.isRequired,
    text_type: PropTypes.number,
    type: PropTypes.string,
    image: PropTypes.string,
    name: PropTypes.string,
    width: PropTypes.number,
    height: PropTypes.number,
    rotate: PropTypes.number,
    responsive_ratio: PropTypes.number,
  }).isRequired,

  design: PropTypes.shape({
    shape_color: PropTypes.string,
    shape_start_color: PropTypes.string,
    shape_ending_color: PropTypes.string,
    shape_gradient_direction: PropTypes.number,
    text_color: PropTypes.string,
    font_family: PropTypes.string,
    font_style: PropTypes.shape({
      weight: PropTypes.string,
      style: PropTypes.string,
      decoration: PropTypes.string,
    }),
    border: PropTypes.shape({
      style: PropTypes.string,
      width: PropTypes.number,
      color: PropTypes.string,
    }),
    border_radius: PropTypes.number,
    shape_class: PropTypes.string,
  }).isRequired,

  type: PropTypes.string,
};

LabelPreview.defaultProps = {
  data: {
    text: "",
    text_type: 0,
    type: "text",
    image: null,
    name: "",
    width: 30,
    height: 10,
    rotate: 0,
    responsive_ratio: 0.34,
  },
  design: {
    shape_color: "#B9B9B9",
    shape_start_color: "#B9B9B9",
    shape_ending_color: "#E5E5E5",
    shape_gradient_direction: 90,
    text_color: "#5E5873",
    font_family: "default",
    font_style: {
      weight: "bold",
      style: "italic",
      decoration: "underline",
    },
    border: {
      style: "none",
      width: 0,
      color: "#000000",
    },
    border_radius: 0,
    shape_class: "pl-text-rectangle",
  },
};

export default LabelPreview;
